// Visualization Base (researcher-only "Base" section) — restricts the eligible
// respondent pool for a chart's data using AND-joined conditions built from
// stable question/variable/value IDs (never labels, which can change).
// Shared across chart types (bar, and eventually others) since the underlying
// condition-building UI, validation, and /search filter transform are all
// chart-agnostic — only the Redux slice each chart type stores it in differs.
export type VisualizationBaseQuestionType = "single" | "multi";

/**
 * Where a widget's active base gets its definition:
 * - "custom": widget-scoped conditions authored inline (the original behavior).
 * - "preconfigured": references a Dataset-Level Base by id; conditions are
 *   resolved from the dataset at query time, never copied onto the widget.
 * Absent = "custom" (existing saved bases need no migration).
 */
export type VisualizationBaseSource = "custom" | "preconfigured";

export interface VisualizationBaseCondition {
  id: string;
  questionId: string;
  questionType: VisualizationBaseQuestionType;
  // Multiple: selected variable IDs. Single grid: row-variable IDs (paired with valueIds).
  variableIds: string[];
  // Single: selected value IDs. Single grid: column-value IDs (paired with variableIds).
  valueIds: string[];
}

export interface VisualizationBase {
  enabled: boolean;
  /** The custom-base draft name; preserved across mode switches. */
  name: string;
  conditions: VisualizationBaseCondition[];
  source?: VisualizationBaseSource;
  /** Set when source === "preconfigured": the DatasetBase.id this widget links to. */
  preconfiguredBaseId?: string;
  /**
   * Cached display name of the linked Dataset-Level Base, kept separate from
   * `name` so switching back to Custom restores the custom name intact. Editors
   * resolve the live name; footers/exports fall back to this cache.
   */
  preconfiguredBaseName?: string;
}

export const DEFAULT_VISUALIZATION_BASE: VisualizationBase = {
  enabled: false,
  name: "",
  conditions: [],
  source: "custom",
};

/** Absent source is treated as "custom" so pre-source saved bases keep working. */
export function getVisualizationBaseSource(
  base: VisualizationBase | undefined | null,
): VisualizationBaseSource {
  return base?.source ?? "custom";
}

/** Name to show in footers/exports: the linked base's cached name when pre-configured, else the custom name. */
export function getVisualizationBaseDisplayName(
  base: VisualizationBase | undefined | null,
): string {
  if (!base) return "";
  return getVisualizationBaseSource(base) === "preconfigured"
    ? (base.preconfiguredBaseName ?? "")
    : base.name;
}
